/**
 * The MiniMap allows to show smaller map with low zoom level located in a corner of main map. It is based on
 * [Leaflet-MiniMap plugin](https://github.com/Norkart/Leaflet-MiniMap). The default plugin functionality is
 * integrated with TomTom SDK.
 *
 * The tomtom.miniMap() method creates a new instance of MiniMap widget which can be added to the map.
 *
 * @example
 * ```javascript
 * tomtom.key("<your-tomtom-API-key>");
 * var miniMapOptions = {height: 90, width: 120};
 * var map = tomtom.map("map");
 * var miniMapControl = tomtom.miniMap(miniMapOptions);
 * miniMapControl.addTo(map);
 * ```
 *
 * @module Widgets
 * @class minimap
 */
define('widgets/views/miniMap',['display/map', 'leaflet-minimap'],
    function(L, MiniMap) {
        'use strict';
        var MAP_LAYER_DEFAULT_OPTIONS = {
                minZoom: 2,
                maxZoom: 10
            },
            CONTROL_DEFAULT_OPTIONS = {
                toggleDisplay: true,
                zoomAnimation: true,
                autoToggleDisplay: true,
                zoomLevelOffset: -5
            };

        function triggerInvalidateSize() {
            var baseLayers, baseLayer;
            baseLayers = this._miniMap.getBaseLayers();
            for (baseLayer in baseLayers) {
                if (baseLayers.hasOwnProperty(baseLayer)) {
                    baseLayers[baseLayer].once('add', function() {
                        this._miniMap.invalidateSize();
                    }, this);
                    baseLayers[baseLayer].once('load', function() {
                        this._miniMap.invalidateSize();
                    }, this);
                }
            }
        }

        L.TomTomMinimap = MiniMap.extend({
            /**
             * Method returns current zoom level of a mini map.
             *
             * @method getZoom
             * @return {Number} Returns current zoom level
             */
            getZoom: function() {
                return this._miniMap.getZoom();
            },
            /**
             * Method for adding listeners for a particular event type. Event handlers for events with 'map-' prefix
             * are added to minimap map instance directly.
             *
             * @method on
             * @return {Object} Returns reference to current instance of minimap widget
             */
            on: function(eventName, eventHandler) {
                var regexMatch, mapEventName;
                if (!eventName || !eventHandler) {
                    return this;
                }
                regexMatch = /^map-(.+)$/.exec(eventName);
                if (regexMatch) {
                    mapEventName = regexMatch[1];
                    this._miniMap.on(mapEventName, eventHandler);
                }
                return this;
            },
            _getMinimapBaseLayer: function(source) {
                return this._miniMap.getBaseLayers()[source || this.options.mapOptions.source[0]];
            },
            /**
             * Method allows to change a source data for currently shown mini map. It should be one of sources provided
             * at creation as a property 'options.mapOptions.source'
             *
             * @method setMapSource
             * @param {String} source a source of data for a mini map
             */
            setMapSource: function(source) {
                this._miniMap.removeLayer(this._layer);
                this._miniMap.setMapSource(source);
                this._layer = this._getMinimapBaseLayer(source);
            },
            initialize: function(options) {
                MiniMap.prototype.initialize.call(this, L.tomTomLayer(options.mapOptions), options);
            },
            onAdd: function(map) {
                var result = MiniMap.prototype.onAdd.call(this, map);
                //Need to trigger size invalidation because without it sometimes tiles are not loaded
                triggerInvalidateSize.call(this);
                return result;
            }
        });

        /**
         * Method creates a new instance of MiniMap plugin. The options can be passed on creation.
         *
         * @method miniMap
         * @param {Object} [options={}] Options to be passed to the MiniMap.
         * @param {String} [options.position='bottomright'] The standard Leaflet.Control position parameter,
         *                  used like all the other controls. Defaults to 'bottomright'.
         * @param {Integer} [options.width=150] The width of the minimap in pixels.
         * @param {Integer} [options.height=150] The height of the minimap in pixels.
         * @param {Integer} [options.collapsedWidth=19] The width of the toggle marker and the minimap
         *                  when collapsed, in pixels.
         * @param {Integer} [options.collapsedHeight=19] The height of the toggle marker and the minimap
         *                  when collapsed, in pixels.
         * @param {Integer} [options.zoomLevelOffset=-8] The offset applied to the zoom in the minimap compared
         *                  to the zoom of the main map. Can be positive or negative.
         * @param {Integer} [options.zoomLevelFixed=null] Overrides the offset to apply a fixed zoom level
         *                  to the minimap regardless of the main map zoom. Set it to any valid zoom level.
         *                  If unset zoomLevelOffset is used instead.
         * @param {Boolean} [options.centerFixed=false] Applies a fixed position to the minimap regardless
         *                  of the main map's view / position. Prevents panning the minimap, but does allow zooming
         *                  (both in the minimap and the main map). If the minimap is zoomed, it will always
         *                  zoom around the centerFixed point. You can pass in a LatLng-equivalent object.
         * @param {Boolean} [options.zoomAnimation=false] Sets whether the minimap should have an animated zoom.
         *                  (Will cause it to lag a bit after the movement of the main map.)
         * @param {Boolean} [options.toggleDisplay=false] Sets whether the minimap should have a button to minimise it.
         * @param {Boolean} [options.autoToggleDisplay=false] Sets whether the minimap should hide automatically
         *                  if the parent map bounds does not fit within the minimap bounds.
         *                  Especially useful when 'zoomLevelFixed' is set.
         * @param {Boolean} [options.minimized=false] Sets whether the minimap should start in a minimized position.
         * @param {Object} [options.aimingRectOptions] Sets the style of the aiming rectangle by passing in
         *                  a Path.Options (http://leafletjs.com/reference.html#path-options) object.
         *                  (Clickable will always be overridden and set to false.)
         * @param {Object} [options.shadowRectOptions] Sets the style of the aiming shadow rectangle by passing in
         *                  a Path.Options (http://leafletjs.com/reference.html#path-options) object.
         *                  (Clickable will always be overridden and set to false.)
         * @param {Object} [options.strings] Overrides the default strings allowing for translation.
         * @param {Object} [options.mapOptions] Sets Leaflet options for the MiniMap map.
         *                  It does not override the MiniMap default map options but extends them.
         *                  See regular map options for more details.
         *
         * @example
         * ```javascript
         *
         * var miniMapOptions = {
         *  height: 90,
         *  width: 120,
         *  zoomLevelOffset: -8,     // zoom difference between main- and mini-maps (defaults to -8)
         *
         *  // below options have default values:
         *  toggleDisplay: true,     // show a button to hide/show mini-map
         *  zoomAnimation: true,     // animate zoom changes
         *  autoToggleDisplay: true, // hide mini-map automatically if it's not needed
         *  mapOptions: {
         *   source: ["raster", "vector"],
         *   glyphsUrl: window.location.protocol + "//" + window.location.host
         *              + "/glyphs/{fontstack}/{range}.pbf",
         *   spriteUrl: window.location.protocol + "//" + window.location.host
         *              + "/sprites/sprite",
         *   minZoom: 2,          // min zoom of mini-map
         *   maxZoom: 4,          // max zoom of mini-map
         *   }
         * };
         *
         * var miniMapControl = tomtom.miniMap(miniMapOptions);
         * miniMapControl.addTo(map);
         * ```
         */
        return function(options) {

            if (!options) {
                options = {};
            }

            options.mapOptions = L.extend(MAP_LAYER_DEFAULT_OPTIONS, options.mapOptions);
            options = L.extend(CONTROL_DEFAULT_OPTIONS, options);

            if (options.mapOptions.minZoom > options.mapOptions.maxZoom) {
                options.mapOptions.minZoom = options.mapOptions.maxZoom;
            }

            return new L.TomTomMinimap(options);
        };
    });

